import React, { useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createTrip } from '@coparent/shared/src/services/custodyService';
import { HiX, HiOutlinePaperAirplane } from 'react-icons/hi';
import toast from 'react-hot-toast';

const today = () => format(new Date(), 'yyyy-MM-dd');

// Plan a trip / vacation block: assign a whole date range to one parent for the
// co-parent to approve once (mirrors the single-day CustodyDayOverride flow).
const TripModal = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { familyId, family, isHouseholdMember } = useFamily();

  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [assignedParentId, setAssignedParentId] = useState(currentUser.uid);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const parentIds = family?.parentIds || [];
  const parentLabel = (pid) =>
    family?.settings?.parentLabels?.[pid] ||
    family?.parentProfiles?.[pid]?.displayName ||
    'Parent';

  const invalidRange = !startDate || !endDate || endDate < startDate;

  const handlePropose = async () => {
    if (!familyId) return;
    if (invalidRange) {
      toast.error('End date must be on or after the start date');
      return;
    }
    setLoading(true);
    try {
      await createTrip(familyId, {
        startDate,
        endDate,
        assignedParentId,
        originalParentId: currentUser.uid,
        title,
        reason: note,
        proposedBy: currentUser.uid,
      });
      toast.success('Trip proposed!');
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to propose trip');
    }
    setLoading(false);
  };

  if (isHouseholdMember) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-ink-900">Plan a trip</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
              <HiX className="text-lg" />
            </button>
          </div>
          <p className="text-sm text-ink-400 text-center py-4">Trips can only be proposed by the primary co-parents.</p>
          <button onClick={onClose} className="btn-secondary w-full mt-2">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">Plan a trip</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <div className="flex items-center gap-3 p-3.5 bg-parent1-50/60 rounded-xl mb-5 border border-parent1-100">
          <HiOutlinePaperAirplane className="text-parent1-600 text-lg rotate-45" />
          <div>
            <p className="text-sm font-semibold text-ink-900">Block time for a trip or vacation</p>
            <p className="text-xs text-ink-400">
              Custody for the whole range goes to the chosen parent once your co-parent approves.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Trip name (optional)</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="input-base"
              placeholder="e.g. Beach week, Grandma's for spring break"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="input-base"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">End date</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={e => setEndDate(e.target.value)}
                className="input-base"
              />
            </div>
          </div>
          {invalidRange && (
            <p className="text-xs text-red-500 -mt-2">End date must be on or after the start date.</p>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Who has the kids?</label>
            <select
              value={assignedParentId}
              onChange={e => setAssignedParentId(e.target.value)}
              className="input-base"
            >
              {parentIds.map(pid => (
                <option key={pid} value={pid}>
                  {parentLabel(pid)}{pid === currentUser.uid ? ' (you)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Note (optional)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              className="input-base resize-none"
              placeholder="Add a note..."
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={handlePropose}
            disabled={loading || invalidRange}
            className="btn-primary flex-1"
          >
            {loading ? 'Sending...' : 'Propose Trip'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripModal;
